'use client'

import {
    CartesianGrid,
    Line,
    LineChart,
    XAxis,
    YAxis,
    ResponsiveContainer,
    Brush,
    Tooltip,
    ReferenceLine,
    ReferenceArea,
    Legend,
} from 'recharts'
import { TrendingUp, TrendingDown, MousePointerClick, X, Check } from 'lucide-react'
import type { IResearchedAd } from '@/api/research-jobs/ads/get-researched-ads-query'
import { DateTime } from 'luxon'
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { cn } from '@/utils/styles/cn'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Progress } from '@/components/ui/progress'

type DatasetType = 'reach' | 'ageBreakdown' | 'genderBreakdown'

interface IAdGrowthChartData {
    growth: NonNullable<IResearchedAd['metrics']['trendMetrics']>['growth']
    rawData: unknown
}

interface AdGrowthChartProps {
    data: IAdGrowthChartData
    className?: string
    onClose?: () => void
}

const CHART_COLORS = {
    reach: 'hsl(var(--primary))',
    male: 'hsl(217, 91%, 60%)',
    female: 'hsl(340, 82%, 52%)',
    unknown: 'hsl(0, 0%, 60%)',
    age18_24: 'hsl(142, 71%, 45%)',
    age25_34: 'hsl(217, 91%, 60%)',
    age35_44: 'hsl(27, 96%, 61%)',
    age45_54: 'hsl(262, 83%, 58%)',
    age55_64: 'hsl(340, 82%, 52%)',
    age65_plus: 'hsl(316, 73%, 52%)',
}

const CHART_AREA_FILL = 'hsl(var(--primary) / 0.1)'
const CHART_AREA_STROKE = 'hsl(var(--primary) / 0.3)'
const CHART_REFERENCE_FILL = 'hsl(var(--primary) / 0.15)'

interface ChartDataPoint {
    name: number
    timestamp: number
    date: string
    reach: number
    male?: number
    female?: number
    unknown?: number
    age18_24?: number
    age25_34?: number
    age35_44?: number
    age45_54?: number
    age55_64?: number
    age65_plus?: number
    [key: string]: any
}

export function AdGrowthChart({ data, className, onClose }: AdGrowthChartProps) {
    const rawData = data.rawData as {
        timestamps: number[]
        values: number[]
        genderBreakdown?: {
            timestamps: number[]
            series: Record<string, number[]>
        }
        ageBreakdown?: {
            timestamps: number[]
            series: Record<string, number[]>
        }
    }
    const hasAnyData = rawData && rawData.timestamps.length > 0
    const chartContainerRef = useRef<HTMLDivElement>(null)

    const [activeDataset, setActiveDataset] = useState<DatasetType>('reach')

    const initialVisibleSeries = useMemo(() => {
        const series = ['reach']
        if (rawData.genderBreakdown) {
            Object.keys(rawData.genderBreakdown.series).forEach((key) => series.push(key))
        }
        if (rawData.ageBreakdown) {
            Object.keys(rawData.ageBreakdown.series).forEach((key) => series.push(`age${key}`))
        }
        return series
    }, [rawData])

    const [visibleSeries, setVisibleSeries] = useState<string[]>(initialVisibleSeries)

    const initialGrowthData = useMemo(() => {
        const data: Record<string, number | null> = { reach: null }
        if (rawData.genderBreakdown) {
            Object.keys(rawData.genderBreakdown.series).forEach((key) => {
                data[key] = null
            })
        }
        if (rawData.ageBreakdown) {
            Object.keys(rawData.ageBreakdown.series).forEach((key) => {
                data[`age${key}`] = null
            })
        }
        return data
    }, [rawData])

    const [growthData, setGrowthData] = useState<Record<string, number | null>>(initialGrowthData)

    const demographicData = useMemo(() => {
        if (!hasAnyData) return { ageBreakdown: [], genderBreakdown: [] }

        const hasGenderBreakdown =
            !!rawData.genderBreakdown &&
            rawData.genderBreakdown.timestamps.length > 0 &&
            Object.keys(rawData.genderBreakdown.series).length > 0

        const hasAgeBreakdown =
            !!rawData.ageBreakdown &&
            rawData.ageBreakdown.timestamps.length > 0 &&
            Object.keys(rawData.ageBreakdown.series).length > 0

        let genderBreakdown: Array<Record<string, any>> = []
        if (hasGenderBreakdown && rawData.genderBreakdown) {
            genderBreakdown = rawData.genderBreakdown.timestamps.map((timestamp, idx) => {
                const dataPoint: Record<string, any> = { timestamp }

                Object.entries(rawData.genderBreakdown!.series).forEach(([gender, values]) => {
                    if (idx < values.length) {
                        dataPoint[gender] = values[idx]
                    }
                })

                return dataPoint
            })
        }

        let ageBreakdown: Array<Record<string, any>> = []
        if (hasAgeBreakdown && rawData.ageBreakdown) {
            ageBreakdown = rawData.ageBreakdown.timestamps.map((timestamp, idx) => {
                const dataPoint: Record<string, any> = { timestamp }

                Object.entries(rawData.ageBreakdown!.series).forEach(([age, values]) => {
                    if (idx < values.length) {
                        const frontendKey = `age${age}`
                        dataPoint[frontendKey] = values[idx]
                    }
                })

                return dataPoint
            })
        }

        return { genderBreakdown, ageBreakdown }
    }, [hasAnyData, rawData])

    const chartData = useMemo(() => {
        if (!hasAnyData) return [] as ChartDataPoint[]

        const timestampToIndex = new Map<number, number>()
        rawData.timestamps.forEach((timestamp, index) => {
            timestampToIndex.set(timestamp, index)
        })

        const baseData = rawData.timestamps.map((timestamp, index) => {
            return {
                name: index,
                timestamp,
                date: DateTime.fromMillis(timestamp).toFormat('dd.MM.yyyy HH:mm'),
                reach: Math.round(rawData.values[index]),
            } as ChartDataPoint
        })

        demographicData.genderBreakdown.forEach((genderPoint) => {
            const timestamp = genderPoint.timestamp
            const index = timestampToIndex.get(timestamp)

            if (index !== undefined && index < baseData.length) {
                Object.entries(genderPoint).forEach(([key, value]) => {
                    if (key !== 'timestamp') {
                        baseData[index][key] = value
                    }
                })
            }
        })

        demographicData.ageBreakdown.forEach((agePoint) => {
            const timestamp = agePoint.timestamp
            const index = timestampToIndex.get(timestamp)

            if (index !== undefined && index < baseData.length) {
                Object.entries(agePoint).forEach(([key, value]) => {
                    if (key !== 'timestamp') {
                        baseData[index][key] = value
                    }
                })
            }
        })

        return baseData
    }, [hasAnyData, rawData, demographicData])

    // Function to calculate growth between two data points
    const calculateGrowth = useCallback(
        (startIndex: number, endIndex: number, dataKey: string) => {
            if (!hasAnyData || startIndex >= endIndex || startIndex < 0 || endIndex >= chartData.length) {
                return null
            }

            const startValue = chartData[startIndex][dataKey]
            const endValue = chartData[endIndex][dataKey]

            if (!startValue || !endValue || startValue <= 0) return 0
            return ((endValue - startValue) / startValue) * 100
        },
        [hasAnyData, chartData]
    )

    // Calculate all growth metrics
    const updateAllGrowthMetrics = useCallback(
        (startIdx: number, endIdx: number) => {
            const newGrowthData: Record<string, number | null> = {}

            newGrowthData.reach = calculateGrowth(startIdx, endIdx, 'reach')

            if (rawData.genderBreakdown) {
                Object.keys(rawData.genderBreakdown.series).forEach((key) => {
                    newGrowthData[key] = calculateGrowth(startIdx, endIdx, key)
                })
            }

            if (rawData.ageBreakdown) {
                Object.keys(rawData.ageBreakdown.series).forEach((key) => {
                    newGrowthData[`age${key}`] = calculateGrowth(startIdx, endIdx, `age${key}`)
                })
            }

            setGrowthData(newGrowthData)
        },
        [calculateGrowth, rawData]
    )

    const [selectedRange, setSelectedRange] = useState<{
        startIndex: number
        endIndex: number
    }>({
        startIndex: 0,
        endIndex: Math.max(0, chartData.length - 1),
    })

    const [initialAnimationComplete, setInitialAnimationComplete] = useState(false)

    // Reset animation when tab changes and start animation timer
    useEffect(() => {
        setInitialAnimationComplete(false)

        const timer = setTimeout(() => {
            setInitialAnimationComplete(true)
        }, 1000)

        return () => clearTimeout(timer)
    }, [activeDataset])

    const [interactiveSelection, setInteractiveSelection] = useState<{
        selecting: boolean
        startX: number | null
        endX: number | null
    }>({
        selecting: false,
        startX: null,
        endX: null,
    })

    const handleBrush = useCallback(
        (e: any) => {
            if (!e || !hasAnyData) return

            if (e.startIndex !== undefined && e.endIndex !== undefined) {
                const startIdx = Math.max(0, e.startIndex)
                const endIdx = Math.min(chartData.length - 1, e.endIndex)

                setSelectedRange({
                    startIndex: startIdx,
                    endIndex: endIdx,
                })

                updateAllGrowthMetrics(startIdx, endIdx)
            }
        },
        [updateAllGrowthMetrics, chartData.length, hasAnyData]
    )

    const handleMouseDown = (e: any) => {
        if (!e || !e.activeLabel) return

        const timestamp = Number.parseInt(e.activeLabel)
        const chartIndex = chartData.findIndex((data) => data.timestamp === timestamp)

        if (chartIndex === -1) return

        setInteractiveSelection({
            selecting: true,
            startX: timestamp,
            endX: timestamp,
        })
    }

    const handleMouseMove = (e: any) => {
        if (!interactiveSelection.selecting || !e || !e.activeLabel) return

        const timestamp = Number.parseInt(e.activeLabel)
        setInteractiveSelection({
            ...interactiveSelection,
            endX: timestamp,
        })
    }

    const handleMouseUp = () => {
        if (!interactiveSelection.selecting || !interactiveSelection.startX || !interactiveSelection.endX) {
            setInteractiveSelection({
                selecting: false,
                startX: null,
                endX: null,
            })
            return
        }

        const startTimeValue = Math.min(interactiveSelection.startX!, interactiveSelection.endX!)
        const endTimeValue = Math.max(interactiveSelection.startX!, interactiveSelection.endX!)

        let startIndex = -1
        let endIndex = -1

        for (let i = 0; i < chartData.length; i++) {
            if (chartData[i].timestamp >= startTimeValue && startIndex === -1) {
                startIndex = i
            }

            if (chartData[i].timestamp >= endTimeValue) {
                endIndex = i
                break
            }
        }

        if (endIndex === -1) {
            endIndex = chartData.length - 1
        }

        if (startIndex !== -1 && endIndex !== -1) {
            setSelectedRange({
                startIndex,
                endIndex,
            })

            updateAllGrowthMetrics(startIndex, endIndex)
        }

        setInteractiveSelection({
            selecting: false,
            startX: null,
            endX: null,
        })
    }

    useEffect(() => {
        if (hasAnyData) {
            updateAllGrowthMetrics(0, chartData.length - 1)
        }
    }, [updateAllGrowthMetrics, chartData.length, hasAnyData])

    const toggleSeries = (series: string) => {
        setVisibleSeries((prev) => (prev.includes(series) ? prev.filter((s) => s !== series) : [...prev, series]))
    }

    const availableTabs = useMemo(() => {
        const tabs = ['reach']
        if (rawData.genderBreakdown && Object.keys(rawData.genderBreakdown.series).length > 0) {
            tabs.push('genderBreakdown')
        }
        if (rawData.ageBreakdown && Object.keys(rawData.ageBreakdown.series).length > 0) {
            tabs.push('ageBreakdown')
        }
        return tabs
    }, [rawData])

    if (!hasAnyData) {
        return null
    }

    const formatXAxisTick = (timestamp: number) => {
        return DateTime.fromMillis(timestamp).toFormat('dd.MM.yyyy')
    }

    const formatBrushTick = () => {
        return ''
    }

    const selectedStartTimestamp = chartData[selectedRange.startIndex]?.timestamp
    const selectedEndTimestamp = chartData[selectedRange.endIndex]?.timestamp

    const referenceAreaStart =
        interactiveSelection.startX && interactiveSelection.endX
            ? Math.min(interactiveSelection.startX, interactiveSelection.endX)
            : null
    const referenceAreaEnd =
        interactiveSelection.startX && interactiveSelection.endX
            ? Math.max(interactiveSelection.startX, interactiveSelection.endX)
            : null

    const getActiveMetrics = () => {
        switch (activeDataset) {
            case 'reach':
                return ['reach']
            case 'genderBreakdown':
                return rawData.genderBreakdown ? ['male', 'female', 'unknown'] : []
            case 'ageBreakdown':
                return rawData.ageBreakdown
                    ? ['age18_24', 'age25_34', 'age35_44', 'age45_54', 'age55_64', 'age65_plus']
                    : []
            default:
                return ['reach']
        }
    }

    const getMetricName = (metric: string) => {
        const names: Record<string, string> = {
            reach: 'Reach',
            male: 'Male',
            female: 'Female',
            unknown: 'Unknown',
            age18_24: '18-24',
            age25_34: '25-34',
            age35_44: '35-44',
            age45_54: '45-54',
            age55_64: '55-64',
            age65_plus: '65+',
        }
        return names[metric] || metric
    }

    const getMetricColor = (metric: string) => {
        return CHART_COLORS[metric as keyof typeof CHART_COLORS] || CHART_COLORS.reach
    }

    return (
        <div className={cn('w-full flex flex-col', className)}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex flex-col">
                    <h2 className="text-xl font-semibold">Reach Development</h2>
                    {chartData.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {chartData[selectedRange.startIndex]?.date} — {chartData[selectedRange.endIndex]?.date}
                        </p>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="rounded-full p-1.5 hover:bg-muted transition-colors"
                            aria-label="Close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>

            {availableTabs.length > 1 && (
                <Tabs
                    defaultValue="reach"
                    value={activeDataset}
                    onValueChange={(value) => setActiveDataset(value as DatasetType)}
                    className="mb-4"
                >
                    <TabsList
                        className={cn('grid w-full', {
                            'grid-cols-2': availableTabs.length === 2,
                            'grid-cols-3': availableTabs.length === 3,
                        })}
                    >
                        <TabsTrigger value="reach">Total Reach</TabsTrigger>
                        {availableTabs.includes('genderBreakdown') && (
                            <TabsTrigger value="genderBreakdown">Gender Breakdown</TabsTrigger>
                        )}
                        {availableTabs.includes('ageBreakdown') && (
                            <TabsTrigger value="ageBreakdown">Age Breakdown</TabsTrigger>
                        )}
                    </TabsList>
                </Tabs>
            )}

            <div className="grid grid-cols-6 gap-3 mb-4">
                {getActiveMetrics().map((metric) => {
                    const growth = growthData[metric]
                    const isPositive = growth !== null && growth >= 0

                    return (
                        <div
                            key={metric}
                            className="flex flex-col p-3 rounded-lg border bg-card cursor-pointer transition-all hover:bg-muted/50"
                            style={{
                                borderLeft: `4px solid ${getMetricColor(metric)}`,
                                opacity: visibleSeries.includes(metric) ? 1 : 0.5,
                            }}
                            onClick={() => toggleSeries(metric)}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium flex items-center gap-1.5">
                                    {getMetricName(metric)}
                                </span>
                                {growth !== null && (
                                    <Badge
                                        variant={isPositive ? 'default' : 'destructive'}
                                        className="flex text-xs py-0.5 px-1.5 items-center gap-1"
                                    >
                                        {isPositive ? '+' : ''}
                                        {Math.abs(growth).toFixed(1).replace('.', ',')}%
                                        {isPositive ? (
                                            <TrendingUp className="h-3 w-3" />
                                        ) : (
                                            <TrendingDown className="h-3 w-3" />
                                        )}
                                    </Badge>
                                )}
                            </div>

                            <div className="mt-1 text-lg font-semibold">
                                {formatNumber(chartData[selectedRange.endIndex]?.[metric])}
                            </div>

                            {growth !== null && (
                                <div className="mt-2">
                                    <Progress
                                        value={Math.min(
                                            100,
                                            Math.max(0, isPositive ? 50 + growth / 2 : 50 - Math.abs(growth) / 2)
                                        )}
                                        className="h-1.5"
                                    />
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            <div className="relative h-[500px] w-full">
                <div className="absolute top-15 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 text-xs bg-background/90 text-muted-foreground py-1 px-2.5 rounded-full shadow-sm border">
                    <MousePointerClick className="h-3 w-3" />
                    <span>Click and drag to select a range</span>
                </div>

                <div ref={chartContainerRef} className="w-full h-full">
                    <ResponsiveContainer className="-mb-2" width="100%" height="100%">
                        <LineChart
                            data={chartData}
                            margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            <CartesianGrid vertical={false} strokeDasharray="3 3" strokeOpacity={0.3} />
                            <XAxis
                                dataKey="timestamp"
                                tickLine={false}
                                axisLine={false}
                                tickMargin={8}
                                tickFormatter={formatXAxisTick}
                                type="number"
                                domain={['auto', 'auto']}
                                scale="time"
                                padding={{ left: 10, right: 10 }}
                                interval="preserveStartEnd"
                                className="text-xs"
                            />
                            <YAxis
                                domain={['auto', 'auto']}
                                tickLine={false}
                                axisLine={false}
                                tickMargin={8}
                                tickFormatter={formatNumber}
                                className="text-xs"
                            />
                            <Tooltip
                                labelFormatter={(value) =>
                                    DateTime.fromMillis(value as number).toFormat('dd.MM.yyyy HH:mm')
                                }
                                formatter={(value, name) => {
                                    if (typeof value === 'number') {
                                        const metricName = getMetricName(name as string)
                                        return [Math.round(value).toLocaleString('de-DE'), metricName]
                                    }
                                    return [String(value), '']
                                }}
                                contentStyle={{
                                    backgroundColor: 'var(--background)',
                                    borderColor: 'var(--border)',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                    padding: '10px 14px',
                                    fontSize: '0.875rem',
                                }}
                                wrapperStyle={{
                                    zIndex: 5,
                                }}
                            />

                            <Legend
                                verticalAlign="top"
                                height={36}
                                formatter={(value, entry) => {
                                    const metric = entry.dataKey as string
                                    return (
                                        <span
                                            className={cn(
                                                'cursor-pointer px-2 py-1 rounded inline-flex items-center gap-1.5 text-sm',
                                                visibleSeries.includes(metric) ? 'opacity-100' : 'opacity-40'
                                            )}
                                            onClick={() => toggleSeries(metric)}
                                        >
                                            <span
                                                className="inline-block w-3 h-3 rounded-full"
                                                style={{ backgroundColor: getMetricColor(metric) }}
                                            />
                                            {getMetricName(metric)}
                                            {visibleSeries.includes(metric) && (
                                                <Check className="h-3 w-3 text-green-500" />
                                            )}
                                        </span>
                                    )
                                }}
                            />

                            {selectedStartTimestamp && selectedEndTimestamp && (
                                <ReferenceArea
                                    x1={selectedStartTimestamp}
                                    x2={selectedEndTimestamp}
                                    fillOpacity={0.1}
                                    stroke={CHART_AREA_STROKE}
                                    fill={CHART_AREA_FILL}
                                />
                            )}

                            {selectedStartTimestamp && (
                                <ReferenceLine
                                    x={selectedStartTimestamp}
                                    stroke={CHART_COLORS.reach}
                                    strokeDasharray="3 3"
                                    label={{
                                        value: 'Start',
                                        position: 'insideTopLeft',
                                        fill: CHART_COLORS.reach,
                                        fontSize: 12,
                                    }}
                                />
                            )}

                            {selectedEndTimestamp && (
                                <ReferenceLine
                                    x={selectedEndTimestamp}
                                    stroke={CHART_COLORS.reach}
                                    strokeDasharray="3 3"
                                    label={{
                                        value: 'End',
                                        position: 'insideTopRight',
                                        fill: CHART_COLORS.reach,
                                        fontSize: 12,
                                    }}
                                />
                            )}

                            {referenceAreaStart !== null && referenceAreaEnd !== null && (
                                <ReferenceArea
                                    x1={referenceAreaStart}
                                    x2={referenceAreaEnd}
                                    strokeOpacity={0.3}
                                    fillOpacity={0.3}
                                    fill={CHART_REFERENCE_FILL}
                                />
                            )}

                            {getActiveMetrics().map(
                                (metric) =>
                                    visibleSeries.includes(metric) && (
                                        <Line
                                            key={metric}
                                            type="monotone"
                                            dataKey={metric}
                                            name={metric}
                                            stroke={getMetricColor(metric)}
                                            strokeWidth={2}
                                            dot={false}
                                            isAnimationActive={!initialAnimationComplete}
                                            animationDuration={initialAnimationComplete ? 0 : 1000}
                                            activeDot={{
                                                r: 6,
                                                fill: getMetricColor(metric),
                                                stroke: 'var(--background)',
                                                strokeWidth: 2,
                                            }}
                                        />
                                    )
                            )}

                            <Brush
                                dataKey="name"
                                height={40}
                                fill="hsl(var(--muted) / 0.5)"
                                stroke="hsl(var(--primary))"
                                travellerWidth={12}
                                onChange={handleBrush}
                                startIndex={selectedRange.startIndex}
                                endIndex={selectedRange.endIndex}
                                tickFormatter={formatBrushTick}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    )
}

function formatNumber(value: number | undefined | null): string {
    if (value === undefined || value === null) return '0'
    const roundedValue = Math.round(value)
    if (roundedValue >= 1000000) return `${Math.floor(roundedValue / 1000000)}M`
    if (roundedValue >= 1000) return `${Math.floor(roundedValue / 1000)}K`
    return roundedValue.toLocaleString('de-DE')
}
